/* Fontes: a página pedida — conferir os datasets de entrada e o que saiu deles.
 *
 * Por arquivo: papel, tamanho, linhas, colunas, sha256, faixa de cobertura, e o
 * selo de divergência quando o disco não bate mais com o que gerou os números. */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { compact, fmtBytes, fmtDateTime, intBR } from "../lib/format";
import { Card, ErrorState, PageHead, Skeleton, Tiles } from "../components/ui";
import { useMeta } from "../components/Shell";
import { SyncPanel } from "../components/SyncPanel";

interface Dataset {
  name: string; role: string; filename: string; path: string; note: string;
  present: number | boolean; present_now: boolean; required: number | boolean;
  bytes: number | null; rows: number | null; columns: string[];
  sha256: string | null; sha256_now: string | null; drift: boolean;
  modified_at: string | null; sum_amount: number | null; distinct_users: number | null;
  coverage_from: string | null; coverage_to: string | null;
}

export function Sources() {
  const { data: meta } = useMeta();
  const datasets = useQuery({
    queryKey: ["datasets"],
    queryFn: () => api<{ generated_at: string; datasets: Dataset[] }>("/api/datasets"),
  });

  const rows = datasets.data?.datasets ?? [];
  const inputs = rows.filter((d) => d.role !== "benchmark");
  const benchmarks = rows.filter((d) => d.role === "benchmark");

  return (
    <>
      <PageHead eyebrow="proveniência" title="Fontes de dados">
        Tudo o que o painel mostra é derivado destes arquivos. O espelho vem do Supabase
        e é regenerável; o snapshot de arranque é versionado no git e o caminho de volta
        é <code>git checkout -- input/</code>.
      </PageHead>

      {meta && (
        <Tiles items={[
          { key: "gerado", label: "Dados gerados em", unit: "n", value: null,
            detail: fmtDateTime(meta.generated_at) },
          { key: "corte", label: "Mês de corte", unit: "n", value: null,
            detail: meta.cut_ym },
          { key: "linhas", label: "Descrições", unit: "n", value: meta.counts.descriptions },
          { key: "drift", label: "Entradas mudaram?", unit: "n", value: null,
            flow: meta.drift ? "DESPESA" : undefined,
            detail: meta.drift ? "sim — sincronize para atualizar" : "não, tudo em dia" },
        ]} />
      )}

      <div style={{ marginTop: 18 }}>
        <SyncPanel />
      </div>

      <div style={{ marginTop: 18 }}>
        <Card title="Datasets de entrada" sub="o espelho é a fonte; o snapshot é a reserva">
          {datasets.isPending && <Skeleton rows={4} height={60} />}
          {datasets.error && (
            <ErrorState error={datasets.error} onRetry={() => datasets.refetch()} />
          )}
          {inputs.map((dataset) => (
            <div key={dataset.name}
                 style={{ padding: "14px 0", borderBottom: "1px solid var(--border-2)" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <strong className="mono">{dataset.filename ?? dataset.name}</strong>
                <span className="tag">{dataset.role}</span>
                {!dataset.present_now && <span className="tag alta">ausente no disco</span>}
                {dataset.drift && <span className="tag alta">mudou desde o build</span>}
              </div>

              <p style={{ fontSize: 12.5, color: "var(--ink-2)", margin: "6px 0 8px",
                          maxWidth: "80ch" }}>
                {dataset.note}
              </p>

              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12,
                            color: "var(--ink-3)" }}>
                <span>{intBR(dataset.rows ?? 0)} linhas</span>
                <span>{fmtBytes(dataset.bytes)}</span>
                <span>{dataset.columns.length} colunas</span>
                {dataset.distinct_users ? <span>{dataset.distinct_users} usuários</span> : null}
                {dataset.sum_amount
                  ? <span>R$ {compact(dataset.sum_amount)} de massa</span> : null}
                {dataset.coverage_from
                  ? <span>{dataset.coverage_from} → {dataset.coverage_to}</span> : null}
                <span>modificado {fmtDateTime(dataset.modified_at)}</span>
              </div>

              <div className="mono" style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>
                sha256 {dataset.sha256?.slice(0, 24) ?? "—"}…
                {dataset.drift && dataset.sha256_now && (
                  <> · agora {dataset.sha256_now.slice(0, 24)}…</>
                )}
              </div>

              {dataset.columns.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--ink-3)" }}>
                    colunas
                  </summary>
                  <div className="mono" style={{ fontSize: 11.5, color: "var(--ink-2)",
                                                 marginTop: 6, lineHeight: 1.7 }}>
                    {dataset.columns.join(" · ")}
                  </div>
                </details>
              )}
            </div>
          ))}
        </Card>
      </div>

      {!!benchmarks.length && (
        <div style={{ marginTop: 18 }}>
          <Card title="Resultados de benchmark"
                sub="entrada, não saída: cada arquivo custou chamadas de LLM reais">
            <table>
              <thead>
                <tr>
                  <th scope="col">modelo</th>
                  <th scope="col" className="num">linhas</th>
                  <th scope="col" className="num">tamanho</th>
                  <th scope="col">sha256</th>
                </tr>
              </thead>
              <tbody>
                {benchmarks.map((dataset) => (
                  <tr key={dataset.name}>
                    <td className="mono">{dataset.filename}</td>
                    <td className="num">{intBR(dataset.rows ?? 0)}</td>
                    <td className="num">{fmtBytes(dataset.bytes)}</td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {dataset.sha256?.slice(0, 16)}…
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {meta && (
        <div style={{ marginTop: 18 }}>
          <Card title="Como estes números foram construídos">
            <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "7px 18px",
                         fontSize: 13, margin: 0 }}>
              <dt style={{ color: "var(--ink-3)" }}>fonte</dt>
              <dd style={{ margin: 0 }} className="mono">{meta.source}</dd>
              <dt style={{ color: "var(--ink-3)" }}>gerador</dt>
              <dd style={{ margin: 0 }} className="mono">versão {meta.generator_version}</dd>
              <dt style={{ color: "var(--ink-3)" }}>mês de corte</dt>
              <dd style={{ margin: 0 }} className="mono">{meta.cut_ym}</dd>
              <dt style={{ color: "var(--ink-3)" }}>invariantes</dt>
              <dd style={{ margin: 0 }}>
                {meta.invariants_ran ? "rodadas neste build" : "puladas neste build"}
              </dd>
              <dt style={{ color: "var(--ink-3)" }}>imagem</dt>
              <dd style={{ margin: 0 }} className="mono">
                {meta.git_sha} · {meta.built_at}
              </dd>
            </dl>
          </Card>
        </div>
      )}
    </>
  );
}
